<template>
  <div class="page-container reveal pb-12">
    <!-- En-tête de page responsive -->
    <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
      <div>
        <h2 class="text-xl sm:text-2xl font-black text-profissa-text-main flex items-center gap-2.5">
          <el-icon class="text-profissa-accent"><UserFilled /></el-icon>
          <span>Mon Compte</span>
        </h2>
        <p class="text-xs sm:text-sm text-profissa-text-dim mt-1">
          Gestion de vos identifiants d'accès et vue d'ensemble de vos autorisations
        </p>
      </div>
      <div class="flex items-center gap-2 self-start sm:self-auto">
        <el-button
          :icon="Refresh"
          circle
          :loading="isLoading"
          @click="loadProfile"
          title="Actualiser les informations"
        />
      </div>
    </div>

    <!-- Loader initial -->
    <div v-if="isLoading && !user" class="flex justify-center p-16">
      <el-icon class="is-loading text-4xl text-profissa-accent"><Loading /></el-icon>
    </div>

    <div v-else class="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
      <!-- Colonne Gauche : Identité & Sécurité (12 col sur mobile, 5 col sur grand écran) -->
      <div class="col-span-12 lg:col-span-5 flex flex-col gap-6">
        <!-- Carte Profil -->
        <el-card class="profile-card !border-profissa-glass-border">
          <div class="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div class="profile-avatar shrink-0">
              <span>{{ userInitial }}</span>
            </div>
            <div class="flex-1 min-w-0">
              <div class="flex flex-wrap items-center gap-2">
                <h3 class="text-lg sm:text-xl font-bold text-profissa-text-main truncate max-w-[220px] sm:max-w-[280px]">
                  {{ user?.full_name || 'Utilisateur' }}
                </h3>
                <el-tag :type="user?.role === 'admin' ? 'danger' : 'warning'" effect="dark" round size="small">
                  {{ user?.role === 'admin' ? 'ADMINISTRATEUR' : (user?.role === 'support' ? 'AGENT SUPPORT' : user?.role?.toUpperCase()) }}
                </el-tag>
              </div>
              <p class="text-sm font-semibold text-profissa-accent mt-1">{{ formatPhone(user?.phone) }}</p>
            </div>
          </div>

          <el-divider class="my-4 !border-profissa-glass-border" />

          <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
            <div>
              <span class="text-profissa-text-dim block text-[0.7rem] uppercase tracking-wider mb-1">Statut</span>
              <span class="inline-flex items-center gap-2 px-2.5 py-1 rounded-full text-xs font-semibold bg-green-500/10 text-green-400 border border-green-500/20">
                <span class="w-1.5 h-1.5 rounded-full bg-green-400"></span>
                Actif
              </span>
            </div>
            <div>
              <span class="text-profissa-text-dim block text-[0.7rem] uppercase tracking-wider mb-1">Identifiant Unique</span>
              <span class="font-mono text-xs text-profissa-text-muted select-all break-all block">
                #{{ user?.id ? `${user.id.slice(0, 13)}...` : 'N/A' }}
              </span>
            </div>
          </div>
        </el-card>

        <!-- Carte Changement de mot de passe -->
        <el-card class="password-card !border-profissa-glass-border">
          <template #header>
            <div class="flex items-center gap-2.5">
              <div class="w-8 h-8 rounded-lg bg-profissa-accent/10 border border-profissa-accent/20 flex items-center justify-center text-profissa-accent">
                <el-icon class="text-base"><Lock /></el-icon>
              </div>
              <div>
                <span class="font-bold text-sm sm:text-base text-profissa-text-main block">Sécurité & Mot de passe</span>
                <span class="text-[0.7rem] text-profissa-text-dim block">Mettre à jour vos identifiants</span>
              </div>
            </div>
          </template>

          <p class="text-xs text-profissa-text-dim mb-5 leading-relaxed">
            Pour renforcer la sécurité de votre compte, choisissez un mot de passe robuste d'au moins 8 caractères contenant majuscules, minuscules et chiffres.
          </p>

          <el-form label-position="top" @submit.prevent="handleChangePassword" class="space-y-4">
            <el-form-item label="Mot de passe actuel" required>
              <el-input
                v-model="pwdForm.current_password"
                type="password"
                show-password
                placeholder="Votre mot de passe actuel"
                autocomplete="current-password"
              />
            </el-form-item>

            <el-form-item label="Nouveau mot de passe" required>
              <el-input
                v-model="pwdForm.new_password"
                type="password"
                show-password
                placeholder="Au moins 8 caractères"
                autocomplete="new-password"
              />
            </el-form-item>

            <el-form-item label="Confirmation du nouveau mot de passe" required>
              <el-input
                v-model="pwdForm.confirm_password"
                type="password"
                show-password
                placeholder="Retapez le nouveau mot de passe"
                autocomplete="new-password"
              />
              <span
                v-if="pwdForm.confirm_password && pwdForm.confirm_password !== pwdForm.new_password"
                class="text-xs text-red-400 mt-1.5 block"
              >
                Les mots de passe ne correspondent pas.
              </span>
            </el-form-item>

            <div class="pt-2">
              <el-button
                type="primary"
                size="large"
                :icon="Check"
                :loading="isUpdatingPassword"
                @click="handleChangePassword"
                class="w-full justify-center font-bold"
              >
                Mettre à jour le mot de passe
              </el-button>
            </div>
          </el-form>
        </el-card>
      </div>

      <!-- Colonne Droite : Permissions & Accréditations (12 col sur mobile, 7 col sur grand écran) -->
      <div class="col-span-12 lg:col-span-7">
        <el-card class="permissions-card !border-profissa-glass-border">
          <template #header>
            <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div class="flex items-center gap-2.5">
                <div class="w-8 h-8 rounded-lg bg-profissa-accent/10 border border-profissa-accent/20 flex items-center justify-center text-profissa-accent">
                  <el-icon class="text-base"><Key /></el-icon>
                </div>
                <div>
                  <span class="font-bold text-sm sm:text-base text-profissa-text-main block">Fonctionnalités & Habilitations</span>
                  <span class="text-[0.7rem] text-profissa-text-dim block">Ce que vous êtes autorisé(e) à réaliser</span>
                </div>
              </div>
              <div class="flex items-center gap-3">
                <el-button
                  size="small"
                  text
                  class="!text-profissa-text-dim hover:!text-profissa-accent !text-[0.75rem]"
                  @click="showTechCodes = !showTechCodes"
                >
                  {{ showTechCodes ? 'Affichage français clair' : 'Mode technique' }}
                </el-button>
                <el-badge :value="userPermissions.length" type="primary" class="shrink-0" />
              </div>
            </div>
          </template>

          <div class="p-3.5 mb-5 rounded-xl bg-profissa-glass-bg border border-profissa-glass-border flex items-start gap-3">
            <el-icon class="text-blue-400 mt-0.5 shrink-0"><InfoFilled /></el-icon>
            <div class="text-xs text-profissa-text-muted leading-relaxed">
              Ces habilitations déterminent les opérations que vous pouvez réaliser sur la plateforme. Elles sont configurées pour votre sécurité par l'administrateur principal.
            </div>
          </div>

          <div class="space-y-5">
            <div
              v-for="group in categorizedPermissions"
              :key="group.title"
              class="permission-group-box"
            >
              <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-1 mb-3">
                <span class="text-xs font-bold uppercase tracking-wider text-profissa-text-dim">
                  {{ group.title }}
                </span>
                <span class="text-[0.7rem] text-profissa-text-dim font-medium">
                  {{ group.items.length }} action(s) autorisée(s)
                </span>
              </div>

              <!-- Liste des badges de permissions avec maquillage en français clair -->
              <div class="flex flex-wrap gap-2">
                <el-tooltip
                  v-for="perm in group.items"
                  :key="perm"
                  :content="showTechCodes ? getPermissionMeta(perm).desc : `${getPermissionMeta(perm).desc} (Identifiant : ${perm})`"
                  placement="top"
                  :show-after="150"
                >
                  <div class="permission-chip">
                    <span class="chip-bullet"></span>
                    <span class="chip-label">
                      {{ showTechCodes ? perm : getPermissionMeta(perm).label }}
                    </span>
                  </div>
                </el-tooltip>
              </div>
            </div>

            <div v-if="!userPermissions.length" class="text-center py-8 text-profissa-text-dim text-sm">
              Aucune permission explicite assignée.
            </div>
          </div>
        </el-card>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, reactive, onMounted } from 'vue'
import { Refresh, Loading, Lock, Check, Key, InfoFilled, UserFilled } from '@element-plus/icons-vue'
import { ElMessage } from 'element-plus'
import { fetchMe, changeMyPassword } from '../services/adminApi'
import { extractApiError } from '../api'
import { formatPhone } from '../utils/format'

const isLoading = ref(false)
const isUpdatingPassword = ref(false)
const user = ref(null)
const showTechCodes = ref(false)

const pwdForm = reactive({
  current_password: '',
  new_password: '',
  confirm_password: '',
})

const userInitial = computed(() => {
  const name = user.value?.full_name || user.value?.phone || 'A'
  return name.charAt(0).toUpperCase()
})

const userPermissions = computed(() => {
  return Array.isArray(user.value?.permissions) ? user.value.permissions : []
})

// Dictionnaire de maquillage : codes techniques vers libellés en français clair et convivial
const PERMISSION_METADATA = {
  // Opérations & Missions
  'dashboard.read': {
    label: 'Tableau de bord général',
    desc: 'Consulter les statistiques d’activité, volumes et indicateurs clés en direct',
  },
  'dispatch.create': {
    label: 'Création de dispatch manuel',
    desc: 'Affecter manuellement une demande client à un artisan qualifié',
  },
  'missions.read.any': {
    label: 'Consultation de toutes les missions',
    desc: 'Suivre le statut, l’avancement et l’historique des interventions',
  },
  'missions.manage.any': {
    label: 'Gestion & clôture des missions',
    desc: 'Forcer la fin, annuler ou intervenir sur le cycle de vie d’une mission',
  },
  'chat.read.any': {
    label: 'Lecture des conversations',
    desc: 'Consulter les messages échangés entre clients, artisans et le support',
  },
  'chat.write.any': {
    label: 'Envoi de messages d’assistance',
    desc: 'Répondre aux clients et prestataires dans les conversations',
  },

  // Gestion des Utilisateurs
  'artisans.read.any': {
    label: 'Consultation des artisans',
    desc: 'Accéder aux profils et coordonnées des artisans inscrits',
  },
  'artisans.review.any': {
    label: 'Validation des dossiers artisans',
    desc: 'Vérifier les pièces, approuver ou refuser les candidatures des artisans',
  },
  'artisans.sanction.any': {
    label: 'Gestion des sanctions',
    desc: 'Appliquer ou lever des avertissements et suspensions d’artisans',
  },
  'clients.read.any': {
    label: 'Consultation des profils clients',
    desc: 'Voir les comptes et l’historique d’utilisation des clients',
  },
  'clients.manage.any': {
    label: 'Gestion des comptes clients',
    desc: 'Bloquer, débloquer ou administrer les comptes clients',
  },
  'orgs.read.any': {
    label: 'Consultation des comptes Pro (B2B)',
    desc: 'Voir les entreprises clientes et organisations partenaires',
  },
  'orgs.manage.any': {
    label: 'Gestion des comptes Pro (B2B)',
    desc: 'Créer et administrer les comptes des organisations partenaires',
  },
  'users.read.any': {
    label: 'Consultation de l’équipe admin',
    desc: 'Voir la liste des gestionnaires et administrateurs',
  },
  'users.create.any': {
    label: 'Création d’accès gestionnaire',
    desc: 'Créer de nouveaux comptes d’accès à l’interface d’administration',
  },
  'users.update.any': {
    label: 'Modification des accès & rôles',
    desc: 'Ajuster les permissions et coordonnées des collaborateurs',
  },
  'users.delete.any': {
    label: 'Suppression d’accès collaborateur',
    desc: 'Supprimer ou révoquer un compte d’administration',
  },
  'users.notes.write.any': {
    label: 'Rédaction de notes internes',
    desc: 'Ajouter des mémos privés confidentiels sur les profils',
  },
  'support.manage': {
    label: 'Gestion de l’équipe support',
    desc: 'Gérer les habilitations des agents d’assistance',
  },

  // Administration & Finance
  'finance.read.any': {
    label: 'Consultation des flux financiers',
    desc: 'Voir les chiffres d’affaires, commissions ProFissa et transactions',
  },
  'finance.manage.any': {
    label: 'Validation des retraits & trésorerie',
    desc: 'Valider les demandes de retrait des artisans vers Mobile Money',
  },
  'categories.manage': {
    label: 'Gestion des métiers & secteurs',
    desc: 'Créer, renommer ou modifier les corps de métier proposés',
  },
  'promos.manage': {
    label: 'Gestion des codes promotionnels',
    desc: 'Créer des réductions, remises et bons d’achat pour les clients',
  },

  // Support & Sécurité
  'sav.read.any': {
    label: 'Consultation des réclamations SAV',
    desc: 'Lire les demandes et litiges du service après-vente',
  },
  'sav.resolve.any': {
    label: 'Traitement des réclamations SAV',
    desc: 'Résoudre et clôturer les dossiers du service après-vente',
  },
  'disputes.read.any': {
    label: 'Consultation des litiges',
    desc: 'Voir les désaccords déclarés entre clients et prestataires',
  },
  'disputes.resolve.any': {
    label: 'Arbitrage & règlement des litiges',
    desc: 'Prendre une décision d’arbitrage et débloquer les fonds',
  },
  'fraud.read.any': {
    label: 'Surveillance des alertes de fraude',
    desc: 'Visualiser les comportements à risque et alertes automatisées',
  },
  'fraud.resolve.any': {
    label: 'Traitement des alertes de fraude',
    desc: 'Statuer sur les alertes antifraude et neutraliser les abus',
  },
  'fraud.policy.manage': {
    label: 'Configuration des règles antifraude',
    desc: 'Ajuster les critères de détection des risques et seuils de blocage',
  },
  'reports.read.any': {
    label: 'Consultation des signalements usagers',
    desc: 'Voir les signalements déposés par les utilisateurs',
  },

  // Configuration Système
  'settings.read': {
    label: 'Consultation des paramètres généraux',
    desc: 'Voir les tarifs de base, seuils GPS et réglages système',
  },
  'settings.manage': {
    label: 'Modification des paramètres plateforme',
    desc: 'Modifier les tarifs, commissions, périmètres GPS et mode maintenance',
  },
  'sms.logs.read': {
    label: 'Historique des SMS expédiés',
    desc: 'Suivre les envois et vérifier la réception des SMS transactionnels',
  },
  'sms.policy.manage': {
    label: 'Règles d’envoi des SMS',
    desc: 'Activer ou désactiver l’envoi de SMS automatiques',
  },
  'sms.broadcast.send': {
    label: 'Envoi de SMS groupés (Annonces)',
    desc: 'Diffuser des messages d’information collectifs par SMS',
  },
  'audit.read.any': {
    label: 'Journal de sécurité & d’audit',
    desc: 'Consulter l’historique certifié des actions menées par l’équipe',
  },
  'errors.read': {
    label: 'Suivi des incidents techniques',
    desc: 'Consulter les rapports de dysfonctionnement envoyés par les apps',
  },
  'errors.update': {
    label: 'Gestion des incidents techniques',
    desc: 'Traiter et marquer la résolution des anomalies signalées',
  },
  'errors.delete': {
    label: 'Suppression des journaux d’incidents',
    desc: 'Purger les anciens rapports d’erreurs techniques',
  },
  'api_keys.read': {
    label: 'Consultation des clés d’API',
    desc: 'Voir les identifiants techniques de connexion sécurisée',
  },
  'api_keys.manage': {
    label: 'Génération de clés d’API',
    desc: 'Créer ou révoquer les clés d’accès techniques pour des partenaires',
  },
}

const getPermissionMeta = (code) => {
  if (PERMISSION_METADATA[code]) {
    return PERMISSION_METADATA[code]
  }
  // Formatage propre automatique en cas de nouvelle permission non cataloguée
  const cleaned = code.replace(/\.any$/, '').replace(/\./g, ' ')
  return {
    label: cleaned.charAt(0).toUpperCase() + cleaned.slice(1),
    desc: `Autorisation d'accès : ${code}`,
  }
}

const PERMISSION_GROUPS = [
  {
    title: 'Opérations & Missions',
    prefix: ['dashboard', 'missions', 'dispatch', 'waitlist', 'chat'],
  },
  {
    title: 'Gestion des Utilisateurs',
    prefix: ['artisans', 'clients', 'orgs', 'users', 'support'],
  },
  {
    title: 'Administration & Finance',
    prefix: ['finance', 'categories', 'promos'],
  },
  {
    title: 'Support & Sécurité',
    prefix: ['sav', 'fraud', 'disputes', 'reports'],
  },
  {
    title: 'Configuration Système',
    prefix: ['settings', 'sms', 'audit', 'errors', 'api_keys'],
  },
]

const categorizedPermissions = computed(() => {
  const perms = userPermissions.value
  const result = []

  for (const group of PERMISSION_GROUPS) {
    const matching = perms.filter((p) =>
      group.prefix.some((prefix) => p === prefix || p.startsWith(prefix + '.'))
    )
    if (matching.length) {
      result.push({
        title: group.title,
        items: matching,
      })
    }
  }

  // Droits résiduels
  const allGrouped = result.flatMap((r) => r.items)
  const remaining = perms.filter((p) => !allGrouped.includes(p))
  if (remaining.length) {
    result.push({
      title: 'Autres autorisations',
      items: remaining,
    })
  }

  return result
})

const loadProfile = async () => {
  isLoading.value = true
  try {
    const data = await fetchMe()
    user.value = data
  } catch (err) {
    ElMessage.error(extractApiError(err))
  } finally {
    isLoading.value = false
  }
}

const handleChangePassword = async () => {
  if (!pwdForm.current_password) {
    ElMessage.warning('Veuillez renseigner votre mot de passe actuel.')
    return
  }
  if (!pwdForm.new_password || pwdForm.new_password.length < 8) {
    ElMessage.warning('Le nouveau mot de passe doit comporter au moins 8 caractères.')
    return
  }
  if (pwdForm.new_password !== pwdForm.confirm_password) {
    ElMessage.warning('Le nouveau mot de passe et sa confirmation ne correspondent pas.')
    return
  }
  if (pwdForm.current_password === pwdForm.new_password) {
    ElMessage.warning('Le nouveau mot de passe doit être différent du mot de passe actuel.')
    return
  }

  isUpdatingPassword.value = true
  try {
    const res = await changeMyPassword({
      current_password: pwdForm.current_password,
      new_password: pwdForm.new_password,
      confirm_password: pwdForm.confirm_password,
    })
    ElMessage.success(res.message || 'Mot de passe modifié avec succès.')
    pwdForm.current_password = ''
    pwdForm.new_password = ''
    pwdForm.confirm_password = ''
  } catch (err) {
    ElMessage.error(extractApiError(err))
  } finally {
    isUpdatingPassword.value = false
  }
}

onMounted(() => {
  loadProfile()
})
</script>

<style scoped>
.profile-card,
.password-card,
.permissions-card {
  border-radius: var(--radius);
}

.profile-avatar {
  width: 58px;
  height: 58px;
  border-radius: 50%;
  background: linear-gradient(135deg, var(--accent), #ff9a44);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  font-weight: 800;
  box-shadow: 0 4px 14px var(--accent-glow);
  flex-shrink: 0;
}

.permission-group-box {
  padding: 14px;
  border-radius: var(--radius-sm);
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid var(--glass-border);
}

/* Pastilles élégantes en français clair */
.permission-chip {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  border-radius: 10px;
  background-color: rgba(255, 255, 255, 0.04);
  border: 1px solid var(--glass-border);
  transition: all 0.2s ease;
  cursor: default;
  max-width: 100%;
}

.permission-chip:hover {
  background-color: rgba(255, 255, 255, 0.08);
  border-color: rgba(255, 107, 0, 0.4);
  transform: translateY(-1px);
}

.chip-bullet {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background-color: #34d399; /* Vert émeraude */
  box-shadow: 0 0 8px rgba(52, 211, 153, 0.5);
  flex-shrink: 0;
}

.chip-label {
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--text-main);
  line-height: 1.3;
}
</style>
